import styles from "./Show.module.scss";
import React, { useEffect, useState } from "react";
import Link from "next/link";
import { useRouter, withRouter } from "next/router";
import { WithRouterProps } from "next/dist/client/with-router";
import {
  DataGrid,
  GridCallbackDetails,
  GridCellParams,
  GridColDef,
  MuiEvent,
} from "@mui/x-data-grid";
import cn from "classnames";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Tooltip,
  Typography,
  Box,
  DialogContentText,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import EmailIcon from '@mui/icons-material/Email';
import ReduceCapacityIcon from '@mui/icons-material/ReduceCapacity';
import Add from "@mui/icons-material/Add";
import { Delete } from "@mui/icons-material";
import dayjs from "dayjs";

import { deleteShow, readShows } from "../../services/ShowService";
import { ShowsResponse } from "../../models/types";
import { ShowInterface } from "../../models/Show";
import { ShowsDashboardDict } from "../../models/ShowDashboard";
import Page from "../../components/Page/Page";
import HealthScoreBar from "../../components/HealthScoreBar/HealthScoreBar";
import ShowsCustomTableToolbar from "../../components/ShowsCustomTableToolbar/ShowsCustomTableToolbar";
import CreateShow from "./CreateShow/CreateShow";
import { readTemplates } from "../../services/TemplateService";
import { TemplateInterface } from "../../models/Template";
import { ShowTemplateType, ShowTemplateTypeNames } from "../../../enums/ShowTemplates";
import { sendTestEmail } from "../../services/NotificationService";
import { Icon } from "src/icons";

interface ShowProps extends WithRouterProps {
  dashboards: ShowsDashboardDict | null;
  companyHealthScore: number;
}

const Show: React.FC<ShowProps> = ({ dashboards, companyHealthScore }) => {
  const [editType, setEditType] = useState("create");
  const [prePopulateShowData, setPrePopulateShowData] =
    useState<ShowInterface>();
  const [deleteShowDialogOpen, setDeleteShowDialogOpen] = useState(false);
  const [snackbarSuccessOpen, setSnackbarSuccessOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [showId, setShowId] = useState(0);
  const [testEmailDialog, setTestEmailDialog] = React.useState(false);
  const [testEmailTemplate, setTestEmailTemplate] = React.useState("");
  const [templates, setTemplates] = React.useState<TemplateInterface[]>()

  const today = dayjs(new Date());

  interface ListShowViewModelConstructorParams {
    showResponse?: ShowsResponse;
    loading?: boolean;
  }
  class ListShowsViewModel {
    showResponse: ShowsResponse | null;
    loading: boolean | null;

    constructor({ showResponse, loading }: ListShowViewModelConstructorParams) {
      this.showResponse = showResponse || null;
      this.loading = loading || null;
    }
  }

  const [viewModel, setViewModel] = useState(new ListShowsViewModel({}));
  const router = useRouter();

  const columns: GridColDef[] = [
    { field: "name", headerName: "Name", flex: 2, maxWidth: 300, minWidth: 100 },
    {
      field: "audienceLink",
      headerName: "Audience Link",
      flex: 1,
      maxWidth: 200,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => (
        <Link href={`/audience/${params.row.publicRouteUrL}`} target="_blank">
          <Button sx={{ px: "0" }}>To Audience View</Button>
        </Link>
      ),
    },
    {
      field: "termsAndConditionsLink",
      headerName: "Terms and Conditions",
      flex: 1,
      maxWidth: 230,
      minWidth: 100,
      sortable: false,
      renderCell: (params) => (
        <>
          {params.row.termsAndConditionsId ? (
            <Link
              href={`/api/backend/termsandconditions?id=${params.row.termsAndConditionsId}`}
              target="_blank"
            >
              <Button sx={{ px: "16px" }}>Terms and Conditions</Button>
            </Link>
          ) : (
            <Typography variant="caption" sx={{ px: "0" }}>
              No Terms and Conditions
            </Typography>
          )}
        </>
      ),
    },
    {
      field: "healthscore",
      headerName: "Health Score",
      description:
        "The show health score represents the average of the sessions health score",
      sortable: false,
      minWidth: 100,
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ px: "0", width: 160 }} data-cy="show-healthscore">
          <HealthScoreBar
            score={dashboards?.[params.row.id]?.showHealthScore!}
          />
        </Box>
      ),
    },
    {
      field: "goToScannerList",
      headerName: "",
      maxWidth: 50,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title="Show QR code scanner list" placement="top">
          <Link href={`/scan/${params.row.scanId}`} target="_blank">
            <IconButton>
              <Icon icon="Qrcode" data-cy="scannerListIcon"/>
            </IconButton>
          </Link>
        </Tooltip>
      ),
    },
    {
      field: "goToAllocations",
      headerName: "",
      maxWidth: 50,
      sortable: false,
      renderCell: () => (
        <IconButton>
          <Tooltip title="Show allocations" placement="top">
            <ReduceCapacityIcon
              data-cy="allocationsIcon"
              className={cn(styles.editIcon, "allocationsIcon")}
            />
          </Tooltip>
        </IconButton>
      ),
    },
    {
      field: "goToAllocationsV2",
      headerName: "",
      maxWidth: 50,
      sortable: false,
      renderCell: (params) => (
        <IconButton>
          <Tooltip title="Show allocations V2" placement="top">
            <Link href={`/allocations-v2/${params.row.publicRouteUrL}/table`} target="_blank">
              <IconButton>
                <Icon icon={"UserGroup"} data-cy="allocationsV2Icon"></Icon>
              </IconButton>
            </Link>
          </Tooltip>
        </IconButton>
      ),
    },
    {
      field: "sendEmail",
      headerName: "",
      maxWidth: 50,
      sortable: false,
      renderCell: () => (
        <IconButton>
          <Tooltip title="Send test email" placement="top">
            <EmailIcon
              data-cy="showsEmailIcon"
              className={cn(styles.editIcon, "emailShowIcon")}
            />
          </Tooltip>
        </IconButton>
      ),
    },
    {
      field: "edit",
      headerName: "",
      maxWidth: 50,
      sortable: false,
      renderCell: () => (
        <IconButton>
          <Tooltip title="Edit show" placement="top">
            <EditIcon
              data-cy="showsEditIcon"
              className={cn(styles.editIcon, "editShowIcon")}
            />
          </Tooltip>
        </IconButton>
      ),
    },
    {
      field: "delete",
      headerName: "",
      maxWidth: 50,
      align: "center",
      sortable: false,
      renderCell: (params) => (
        <IconButton disabled={!params.row.isDeletable} data-cy="delete-show-button">
          <Tooltip title="Delete show" placement="top">
            <Delete data-cy="deleteIcon" className={styles.deleteIcon} />
          </Tooltip>
        </IconButton>
      ),
    },
  ];

  const fetchTemplates = async () => {
    const { data } = await readTemplates();
    if (data) {
      setTemplates(data);
    }
  };

  useEffect(() => {
    fetchTemplates();
    try {

      setViewModel({ ...viewModel, loading: true });
      readShows().then((showResponse) => {
        setViewModel({ showResponse, loading: false });
      });
    } catch (error) {
      if (error) {
        setViewModel({ showResponse: null, loading: false });
      }
    }
  }, []);
  const handleSendTestEmail = (values: any) => {
    sendTestEmail(showId, testEmailTemplate).then(() => {
      setTestEmailDialog(false)
    })
  }

  const activateCreateModal = () => {
    setPrePopulateShowData(undefined);
    setEditType("create");
    router.push(`/?create-show`, undefined, { shallow: true, scroll: false });
  };
  const editCreateModal = (data: any) => {
    setEditType("edit");
    router.push(`/?edit-show/${data.id}`, undefined, {
      shallow: true,
      scroll: false,
    });
  };

  const emptyShows: ShowInterface[] = [];

  const handleCellClick = (
    params: GridCellParams,
    event: MuiEvent<React.MouseEvent>,
    details: GridCallbackDetails
  ) => {
    if (params.field === "edit") {
      setPrePopulateShowData(params.row);
      sessionStorage.setItem("prepopulateData", JSON.stringify(params.row));
      setShowId(params.row.id);
      editCreateModal(params);
    } else if (params.field === "delete" && params.row.isDeletable) {
      setShowId(params.row.id);
      setDeleteShowDialogOpen(true);
    } else if (params.field === "name" || params.field === "healthscore") {
      router.push(`/show/${params.row.id}`);
    } else if (params.field === "sendEmail") {
      setShowId(params.row.id);
      setTestEmailDialog(true)
    } else if (params.field === "goToAllocations") {
      router.push(`/allocations/${params.row.publicRouteUrL}`);
    }
  };

  const handleDialogClose = () => {
    setDeleteShowDialogOpen(false);
    router.replace(`/`, undefined, { shallow: true });
  };

  const handleDeleteShow = () => {
    deleteShow(showId).then((res) => {
      setSnackbarSuccessOpen(true);
      setSnackbarMessage("Show Successfully Deleted!");
      router.push(`/`, undefined, { shallow: true, scroll: false });
      setDeleteShowDialogOpen(false);
      reloadShows();
    });
  };

  const reloadShows = () => {
    readShows().then((showResponse) => {
      setViewModel((prev) => ({ ...prev, showResponse }));
    });
  };

  const handleSnackbarClose = (
    event: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }

    setSnackbarSuccessOpen(false);
  };


  return (
    <Page>
      <div className={styles.show}>
        <Typography variant="h3" component="h1" gutterBottom align="center">
          That's The Ticket
        </Typography>

        <div className={styles.table} data-cy="showsDataGrid">
          <div className={styles.tableHeader}>
            <Typography variant="h5" gutterBottom>
              Show list
            </Typography>
            <Button
              variant="outlined"
              onClick={(event) => activateCreateModal()}
              data-cy="add-show-button"
            >
              <Add />
              Add new show
            </Button>
          </div>

          <DataGrid
            getRowId={(row) => row.name}
            rows={viewModel.showResponse?.data || emptyShows}
            rowHeight={43}
            columns={columns}
            pageSize={5}
            rowsPerPageOptions={[5]}
            rowCount={10}
            autoHeight
            paginationMode="server"
            loading={viewModel.loading || false}
            getRowClassName={(item) => {
              return today.diff(item.row.createAt, "d") < 3
                ? styles.highlightedTableRow
                : styles.tableRow;
            }}
            components={{ Toolbar: ShowsCustomTableToolbar }}
            componentsProps={{
              toolbar: {
                companyHealthScore: companyHealthScore
              },
            }}
            onCellClick={handleCellClick}
            sx={{
              "& .MuiDataGrid-columnHeader .MuiDataGrid-columnSeparator": {
                display: "none",
              },
            }}
          />

          <CreateShow
            showId={showId}
            onSuccess={() => reloadShows()}
            type={editType}
            templates={templates}
          />
          <Dialog open={testEmailDialog} onClose={() => setTestEmailDialog(false)}>
            <DialogTitle>Send a Test Email</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Send a Test Email for this show on your email address. Select the template defined below.
              </DialogContentText>
              <FormControl fullWidth style={{ marginTop: 5 }}>
                <InputLabel>Select Template</InputLabel>
                <Select
                  value={testEmailTemplate}
                  label="Select Template"
                  onChange={(event: SelectChangeEvent) => {
                    setTestEmailTemplate(event.target.value as string)
                  }}
                >
                  {Object.entries(ShowTemplateType).map((value, index) => (
                    <MenuItem key={index} value={value[0]}>{ShowTemplateTypeNames[value[1]]}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleSendTestEmail}>Send</Button>
            </DialogActions>
          </Dialog>
        </div>
        <Dialog
          className={styles.attendanceInfo}
          open={deleteShowDialogOpen}
          onClose={handleDialogClose}
          fullWidth={true}
          maxWidth={"xs"}
          data-cy="deleteShowModal"
        >
          <DialogTitle className={styles.title}>Delete Show</DialogTitle>
          <DialogContent className={styles.content}>
            Are you sure you want to delete this show?
          </DialogContent>
          <DialogActions className={styles.actions}>
            <Button
              variant="contained"
              onClick={() => handleDialogClose()}
              data-cy="show-close-btn"
            >
              Close
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => handleDeleteShow()}
              data-cy="show-delete-btn"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
        <Snackbar
          open={snackbarSuccessOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity="success"
            sx={{ width: "100%" }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </div>
    </Page>
  );
};

export default withRouter(Show);
